import { useState, useEffect } from 'react'
import api, { Customer, Organisation } from '../services/api'
import { useAuth } from '../contexts/AuthContext'
import Header from './layout/Header'
import TabNav from './layout/TabNav'
import CampaignsView from './views/CampaignsView'
import SettingsView from './views/SettingsView'
import LoadingSpinner from './common/LoadingSpinner'
import { createLogger } from '../utils/createLogger'

const log = createLogger('MainApp')

interface MainAppProps {
  user: any
  initialTab?: 'campaigns' | 'settings'
}

const MainApp: React.FC<MainAppProps> = ({ user, initialTab = 'campaigns' }) => {
  const [customerData, setCustomerData] = useState<Customer | null>(null)
  const [organisations, setOrganisations] = useState<Organisation[]>([])
  const [selectedOrgId, setSelectedOrgId] = useState<string>('')
  const [activeTab, setActiveTab] = useState<'campaigns' | 'settings'>(initialTab)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const loadOrganisations = async () => {
    try {
      const orgs = await api.organisations.list()
      setOrganisations(orgs)
      
      // Keep selected org if it still exists
      if (selectedOrgId) {
        const stillExists = orgs.some(org => 
          (org.organisation_id || org.id) === selectedOrgId
        )
        if (!stillExists && orgs.length > 0) {
          setSelectedOrgId(orgs[0].organisation_id || orgs[0].id || '')
        }
      } else if (orgs.length > 0) {
        setSelectedOrgId(orgs[0].organisation_id || orgs[0].id || '')
      }
    } catch (error) {
      log.error('Failed to reload organisations', error instanceof Error ? error : undefined)
    }
  }

  useEffect(() => {
    const loadData = async () => {
      try {
        // Get user data from /v1/auth/me which includes customer info
        const userData = await api.auth.me()
        
        // Extract customer data from user response
        if (userData.customer) {
          setCustomerData(userData.customer)
        } else {
          // Fallback: construct customer from user data
          setCustomerData({
            customer_id: userData.customer_id || '',
            company_name: 'My Company',
            account_status: 'active',
            subscription_status: userData.customer?.subscription_status || 'trial',
            subscription_tier: userData.customer?.subscription_tier || 'free',
          } as Customer)
        }

        // Load organisations
        await loadOrganisations()
      } catch (error) {
        log.error('Failed to load account data', error instanceof Error ? error : undefined, { userId: user?.id || user?.email })
        setError('Failed to load account data. Please refresh.')
      } finally {
        setLoading(false)
      }
    }

    if (user) {
      loadData()
    }
  }, [user])

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
        <div className="text-center">
          <LoadingSpinner size="lg" />
          <p className="mt-4 text-gray-600 dark:text-gray-400">Loading your workspace...</p>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
        <div className="max-w-md w-full bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
          <p className="text-red-600 dark:text-red-400 mb-4">{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="w-full bg-blue-600 hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-600 text-white px-4 py-2 rounded transition-colors"
          >
            Reload
          </button>
        </div>
      </div>
    )
  }

  const selectedOrg = organisations.find(org => org.organisation_id === selectedOrgId)
  const isTrialMode = customerData?.subscription_status === 'trial' || 
                      !customerData?.stripe_subscription_id

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Header with org selector */}
      <Header
        user={user}
        customer={customerData}
        organisations={organisations}
        selectedOrgId={selectedOrgId}
        onOrgChange={setSelectedOrgId}
      />

      {/* Simple tab navigation */}
      <TabNav
        activeTab={activeTab}
        onTabChange={setActiveTab}
      />

      {/* Main content area */}
      <main className="max-w-7xl mx-auto px-4 py-6 bg-gray-50 dark:bg-gray-900">
        {activeTab === 'campaigns' && selectedOrg && (
          <CampaignsView
            organisation={selectedOrg}
            customer={customerData!}
            isTrialMode={isTrialMode}
            onOrganisationUpdate={loadOrganisations}
          />
        )}
        
        {activeTab === 'settings' && (
          <SettingsView
            organisation={selectedOrg}
            customer={customerData!}
            user={user}
            organisations={organisations}
          />
        )}
      </main>
    </div>
  )
}

export default MainApp
